import { Button, Flex, Icon, Select, Text, TextField } from "@repo/ui";
import { useTheme } from "@repo/ui";
import { useStyles } from "purse-styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Modal } from "react-aria-components";
import { toast } from "sonner";
import { useApiClient } from "../../../../contexts/ApiContext";
import type {
	Location,
	LocationId,
	Organization,
	OrganizationId,
	Provider,
	ProviderId,
	ProviderSearchFilters,
} from "../../types";
import { ConfirmDialog } from "./ConfirmDialog";
import { ProviderRow } from "./ProviderRow";
import { US_STATES } from "./usStates";
import {
	removeLocation,
	removeOrganization,
	updateLocation,
	updateOrganization,
	validateProvider,
} from "./utils";

/**
 * Editing goes straight to the shared provider directory through
 * `saveProvidersFromModal`; the modal saves each provider itself rather than
 * handing changes back to its caller. It previously also accepted `onSave`,
 * `isSaving` and `readOnly` and ignored all three, which made callers believe
 * they could observe saves or open it read-only.
 */
export type ProviderNestedModalProps = {
	isOpen: boolean;
	onClose: () => void;
	initialFilters?: Partial<ProviderSearchFilters>;
};

export function ProviderNestedModal({
	isOpen,
	onClose,
	initialFilters,
}: ProviderNestedModalProps) {
	const theme = useTheme();
	const apiClient = useApiClient();

	const [_providers, setProviders] = useState<Provider[]>([]);
	const [draftProviders, setDraftProviders] = useState<Provider[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [_totalProviders, setTotalProviders] = useState(0);

	const [searchTerm, setSearchTerm] = useState(
		initialFilters?.searchTerm ?? "",
	);
	const [cityFilter, setCityFilter] = useState(initialFilters?.city ?? "");
	const [stateFilter, setStateFilter] = useState(initialFilters?.state ?? "");

	const [pendingSearchTerm, setPendingSearchTerm] = useState(
		initialFilters?.searchTerm ?? "",
	);
	const [pendingCityFilter, setPendingCityFilter] = useState(
		initialFilters?.city ?? "",
	);
	const [pendingStateFilter, setPendingStateFilter] = useState(
		initialFilters?.state ?? "",
	);

	const [currentPage, setCurrentPage] = useState(1);
	const providersPerPage = 20;

	const [expandedProviderIds, setExpandedProviderIds] = useState<ProviderId[]>(
		[],
	);
	const [editingProviderIds, setEditingProviderIds] = useState<ProviderId[]>(
		[],
	);
	const [expandedOrgIds, setExpandedOrgIds] = useState<
		Record<ProviderId, OrganizationId[]>
	>({});
	const [pendingDeleteOrg, setPendingDeleteOrg] = useState<
		{ providerId: ProviderId; orgId: OrganizationId } | undefined
	>(undefined);
	const [pendingDeleteLocation, setPendingDeleteLocation] = useState<
		| { providerId: ProviderId; orgId: OrganizationId; locationId: LocationId }
		| undefined
	>(undefined);
	const [validationErrors, setValidationErrors] = useState<
		Record<string, string>
	>({});
	const [originalProviderSnapshots, setOriginalProviderSnapshots] = useState<
		Record<ProviderId, Provider>
	>({});
	const [savingProviderIds, setSavingProviderIds] = useState<ProviderId[]>([]);
	// Removing an organization or location only filters the local draft, which the
	// save cannot distinguish from a payload that never mentioned the row. These
	// are the removals to state explicitly when the provider is saved.
	const [pendingRemovals, setPendingRemovals] = useState<
		Record<
			ProviderId,
			{ locationIds: LocationId[]; organizationIds: OrganizationId[] }
		>
	>({});

	const recordRemoval = useCallback(
		(
			providerId: ProviderId,
			removal: {
				locationIds?: LocationId[];
				organizationIds?: OrganizationId[];
			},
		) => {
			setPendingRemovals((prev) => {
				const current = prev[providerId] ?? {
					locationIds: [],
					organizationIds: [],
				};
				return {
					...prev,
					[providerId]: {
						locationIds: [
							...new Set([
								...current.locationIds,
								...(removal.locationIds ?? []),
							]),
						],
						organizationIds: [
							...new Set([
								...current.organizationIds,
								...(removal.organizationIds ?? []),
							]),
						],
					},
				};
			});
		},
		[],
	);

	const clearRemovals = useCallback((providerId: ProviderId) => {
		setPendingRemovals((prev) => {
			const { [providerId]: _, ...rest } = prev;
			return rest;
		});
	}, []);

	const fetchProviders = useCallback(
		async (filters: ProviderSearchFilters) => {
			setIsLoading(true);
			try {
				const result = await apiClient.tools.listProvidersForModal({
					searchTerm: filters.searchTerm || undefined,
					city: filters.city || undefined,
					state: filters.state || undefined,
					limit: 100,
					offset: 0,
				});
				setProviders(result.providers);
				setDraftProviders(result.providers);
				setTotalProviders(result.total);
				// A new result set replaces the drafts these were staged against, so
				// anything still pointing at the old ones has to go with them. A
				// removal that outlived its draft would be sent on the next save of
				// that provider and unlink a location visible on screen.
				setPendingRemovals({});
				setEditingProviderIds([]);
				setOriginalProviderSnapshots({});
				setValidationErrors({});
			} catch (error) {
				console.error("Failed to fetch providers:", error);
				toast.error("Failed to load providers");
			} finally {
				setIsLoading(false);
			}
		},
		[apiClient],
	);

	useEffect(() => {
		if (isOpen) {
			void fetchProviders({ searchTerm, city: cityFilter, state: stateFilter });
		}
		// Only fetch on modal open, handleSearch handles subsequent fetches
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const handleSearch = useCallback(() => {
		// fetchProviders drops any in-progress edit along with the draft it belongs
		// to. Say so rather than letting typed changes disappear into a new result
		// set, which is the failure this screen is being fixed for.
		if (editingProviderIds.length > 0) {
			toast.info("Unsaved provider changes were discarded");
		}
		setSearchTerm(pendingSearchTerm);
		setCityFilter(pendingCityFilter);
		setStateFilter(pendingStateFilter);
		setCurrentPage(1);
		void fetchProviders({
			searchTerm: pendingSearchTerm,
			city: pendingCityFilter,
			state: pendingStateFilter,
		});
	}, [
		pendingSearchTerm,
		pendingCityFilter,
		pendingStateFilter,
		fetchProviders,
		editingProviderIds,
	]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") {
				handleSearch();
			}
		},
		[handleSearch],
	);

	const totalPages = Math.ceil(draftProviders.length / providersPerPage);
	const paginatedProviders = useMemo(() => {
		const startIndex = (currentPage - 1) * providersPerPage;
		return draftProviders.slice(startIndex, startIndex + providersPerPage);
	}, [draftProviders, currentPage, providersPerPage]);

	const updateProviderById = useCallback(
		(providerId: ProviderId, updater: (p: Provider) => Provider) => {
			setDraftProviders((prev) =>
				prev.map((p) => (p.id === providerId ? updater(p) : p)),
			);
		},
		[],
	);

	const handleToggleProviderExpand = useCallback((providerId: ProviderId) => {
		setExpandedProviderIds((prev) =>
			prev.includes(providerId)
				? prev.filter((id) => id !== providerId)
				: [...prev, providerId],
		);
	}, []);

	const handleStartEdit = useCallback(
		(providerId: ProviderId) => {
			const provider = draftProviders.find((p) => p.id === providerId);
			if (provider) {
				setOriginalProviderSnapshots((prev) => ({
					...prev,
					[providerId]: JSON.parse(JSON.stringify(provider)),
				}));
			}
			setEditingProviderIds((prev) => [...prev, providerId]);
		},
		[draftProviders],
	);

	const handleCancelEdit = useCallback(
		(providerId: ProviderId) => {
			const original = originalProviderSnapshots[providerId];
			if (original) {
				setDraftProviders((prev) =>
					prev.map((p) => (p.id === providerId ? original : p)),
				);
			}
			setEditingProviderIds((prev) => prev.filter((id) => id !== providerId));
			setOriginalProviderSnapshots((prev) => {
				const { [providerId]: _, ...rest } = prev;
				return rest;
			});
			// Cancelling restores the snapshot, so the removals staged against it are
			// no longer wanted either.
			clearRemovals(providerId);
		},
		[originalProviderSnapshots, clearRemovals],
	);

	const handleSaveEdit = useCallback(
		async (providerId: ProviderId) => {
			const provider = draftProviders.find((p) => p.id === providerId);
			if (!provider) return;

			const errors = validateProvider(provider);
			if (Object.keys(errors).length > 0) {
				setValidationErrors((prev) => ({ ...prev, ...errors }));
				toast.error("Please fix validation errors before saving.");
				return;
			}

			setSavingProviderIds((prev) => [...prev, providerId]);
			try {
				const removals = pendingRemovals[providerId];
				const result = await apiClient.tools.saveProvidersFromModal({
					providers: [
						{
							...provider,
							removedLocationIds: removals?.locationIds,
							removedOrganizationIds: removals?.organizationIds,
						},
					],
				});

				// The endpoint reports a refused write by returning success: false
				// rather than throwing, so the catch below never sees one. Reporting
				// it as saved is how a rejected edit used to look identical to a
				// stored one — including the ownership guards on shared directory rows.
				if (!result.success) {
					toast.error(result.message ?? "Failed to save");
					return;
				}

				const persisted = result.providers.find((p) => p.id === providerId);
				if (!persisted) {
					toast.error("Saved, but the provider could not be read back");
					return;
				}

				toast.success("Saved successfully");
				setEditingProviderIds((prev) => prev.filter((id) => id !== providerId));
				setOriginalProviderSnapshots((prev) => {
					const { [providerId]: _, ...rest } = prev;
					return rest;
				});
				clearRemovals(providerId);
				// Show what was stored, not the draft that was sent. Adopting the
				// local draft here is what made a silently discarded fax look saved.
				setProviders((prev) =>
					prev.map((p) => (p.id === providerId ? persisted : p)),
				);
				setDraftProviders((prev) =>
					prev.map((p) => (p.id === providerId ? persisted : p)),
				);
			} catch {
				toast.error("Failed to save");
			} finally {
				setSavingProviderIds((prev) => prev.filter((id) => id !== providerId));
			}
		},
		[draftProviders, apiClient, pendingRemovals, clearRemovals],
	);

	const handleToggleOrgExpand = useCallback(
		(providerId: ProviderId, orgId: OrganizationId) => {
			setExpandedOrgIds((prev) => {
				const current = prev[providerId] || [];
				const updated = current.includes(orgId)
					? current.filter((id) => id !== orgId)
					: [...current, orgId];
				return { ...prev, [providerId]: updated };
			});
		},
		[],
	);

	const handleOrgChange = useCallback(
		(
			providerId: ProviderId,
			orgId: OrganizationId,
			field: keyof Organization,
			value: string | string[],
		) => {
			updateProviderById(providerId, (p) =>
				updateOrganization(p, orgId, (org) => ({ ...org, [field]: value })),
			);
		},
		[updateProviderById],
	);

	const handleLocationChange = useCallback(
		(
			providerId: ProviderId,
			orgId: OrganizationId,
			locId: LocationId,
			field: keyof Location,
			value: string,
		) => {
			updateProviderById(providerId, (p) =>
				updateLocation(p, orgId, locId, (loc) => ({ ...loc, [field]: value })),
			);
		},
		[updateProviderById],
	);

	const handleRemoveOrgRequest = useCallback(
		(providerId: ProviderId, orgId: OrganizationId) => {
			setPendingDeleteOrg({ providerId, orgId });
		},
		[],
	);

	const handleRemoveLocationRequest = useCallback(
		(providerId: ProviderId, orgId: OrganizationId, locationId: LocationId) => {
			setPendingDeleteLocation({ providerId, orgId, locationId });
		},
		[],
	);

	const confirmRemoveOrg = useCallback(() => {
		if (pendingDeleteOrg) {
			const provider = draftProviders.find(
				(p) => p.id === pendingDeleteOrg.providerId,
			);
			const org = provider?.organizations.find(
				(o) => o.id === pendingDeleteOrg.orgId,
			);
			// The confirm dialog promises to remove the organization "and all its
			// locations", so unlink both rather than leaving the locations attached
			// to the provider with nowhere to show them.
			recordRemoval(pendingDeleteOrg.providerId, {
				organizationIds: [pendingDeleteOrg.orgId],
				locationIds: org?.locations.map((loc) => loc.id) ?? [],
			});
			updateProviderById(pendingDeleteOrg.providerId, (p) =>
				removeOrganization(p, pendingDeleteOrg.orgId),
			);
			setPendingDeleteOrg(undefined);
		}
	}, [pendingDeleteOrg, updateProviderById, draftProviders, recordRemoval]);

	const confirmRemoveLocation = useCallback(() => {
		if (pendingDeleteLocation) {
			recordRemoval(pendingDeleteLocation.providerId, {
				locationIds: [pendingDeleteLocation.locationId],
			});
			updateProviderById(pendingDeleteLocation.providerId, (p) =>
				removeLocation(
					p,
					pendingDeleteLocation.orgId,
					pendingDeleteLocation.locationId,
				),
			);
			setPendingDeleteLocation(undefined);
		}
	}, [pendingDeleteLocation, updateProviderById, recordRemoval]);

	const handleClose = useCallback(() => {
		setPendingSearchTerm("");
		setPendingCityFilter("");
		setPendingStateFilter("");
		setSearchTerm("");
		setCityFilter("");
		setStateFilter("");
		setCurrentPage(1);
		setExpandedProviderIds([]);
		setEditingProviderIds([]);
		setExpandedOrgIds({});
		setValidationErrors({});
		setProviders([]);
		setDraftProviders([]);
		// The confirm dialogs render outside <Modal>, so leaving these set left a
		// dialog on screen after the modal that opened it was gone, still wired to a
		// provider the user could no longer see.
		setPendingDeleteOrg(undefined);
		setPendingDeleteLocation(undefined);
		setOriginalProviderSnapshots({});
		setPendingRemovals({});
		onClose();
	}, [onClose]);

	const modalClassName = useStyles({
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		zIndex: 1000,
	});

	const dialogClassName = useStyles({
		backgroundColor: theme.colors.sand1,
		border: `1px solid ${theme.colors.sand6}`,
		borderRadius: theme.borderRadiuses.md,
		width: "90vw",
		maxWidth: "1200px",
		height: "90vh",
		boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
		outline: "none",
		display: "flex",
		flexDirection: "column",
	});

	const pendingOrgToDelete = pendingDeleteOrg
		? (() => {
				const provider = draftProviders.find(
					(p) => p.id === pendingDeleteOrg.providerId,
				);
				return provider?.organizations.find(
					(o) => o.id === pendingDeleteOrg.orgId,
				);
			})()
		: undefined;

	const pendingLocationToDelete = pendingDeleteLocation
		? (() => {
				const provider = draftProviders.find(
					(p) => p.id === pendingDeleteLocation.providerId,
				);
				const org = provider?.organizations.find(
					(o) => o.id === pendingDeleteLocation.orgId,
				);
				const loc = org?.locations.find(
					(l) => l.id === pendingDeleteLocation.locationId,
				);
				return { org, loc };
			})()
		: undefined;

	return (
		<>
			<Modal
				isDismissable
				isOpen={isOpen}
				onOpenChange={(open) => !open && handleClose()}
				className={modalClassName}
			>
				<Dialog className={dialogClassName} aria-label="Provider list">
					<Flex
						padding="md"
						paddingHorizontal="md"
						align="center"
						justify="space-between"
						borderBottomWidth="md"
						borderColor="sandA5"
					>
						<Flex gap="md" align="center">
							<Text size="md" weight="semibold">
								Provider directory
							</Text>
							{totalPages > 0 && (
								<Text size="md" color="sand10">
									Page {currentPage} of {totalPages}
								</Text>
							)}
						</Flex>
						<Button
							variant="ghost"
							size="xs"
							onPress={handleClose}
							aria-label="Close"
						>
							<Icon name="X" size="sm" />
						</Button>
					</Flex>

					<Flex
						padding="md"
						paddingHorizontal="md"
						borderBottomWidth="md"
						borderColor="sandA4"
						gap="lg"
						onKeyDown={handleKeyDown}
					>
						<Flex gap="xs" align="center" grow>
							<Icon name="Search" size="xs" color="sand10" />
							<TextField
								aria-label="Search providers"
								placeholder="Search providers, orgs, locations..."
								value={pendingSearchTerm}
								onChange={(e) => setPendingSearchTerm(e.target.value)}
								subtlePlaceholder={false}
							/>
						</Flex>
						<Flex gap="xs" align="center">
							<TextField
								aria-label="Filter by city"
								placeholder="City"
								value={pendingCityFilter}
								onChange={(e) => setPendingCityFilter(e.target.value)}
								style={{ width: 120 }}
								subtlePlaceholder={false}
							/>
							<Select
								aria-label="Filter by state"
								placeholder="State"
								options={US_STATES}
								value={pendingStateFilter || undefined}
								onSelectionChange={(value) =>
									setPendingStateFilter(value ?? "")
								}
								size="sm"
								minWidth={140}
							/>
							<Button
								variant="outline"
								size="sm"
								onPress={isLoading ? undefined : handleSearch}
							>
								<Icon name="Search" size="xs" />
							</Button>
						</Flex>
					</Flex>

					<Flex column overflow="y" style={{ flex: 1, minHeight: 0 }}>
						{isLoading ? (
							<Flex padding="xl" justify="center">
								<Text size="md" color="sand11">
									Loading providers...
								</Text>
							</Flex>
						) : draftProviders.length === 0 ? (
							<Flex padding="xl" justify="center">
								<Text size="md" color="sand11">
									No providers found. Try adjusting your search.
								</Text>
							</Flex>
						) : (
							<>
								{paginatedProviders.map((provider) => (
									<ProviderRow
										key={provider.id}
										provider={provider}
										isExpanded={expandedProviderIds.includes(provider.id)}
										isEditing={editingProviderIds.includes(provider.id)}
										isSaving={savingProviderIds.includes(provider.id)}
										expandedOrgIds={expandedOrgIds[provider.id] || []}
										validationErrors={validationErrors}
										onToggleExpand={() =>
											handleToggleProviderExpand(provider.id)
										}
										onStartEdit={() => handleStartEdit(provider.id)}
										onSaveEdit={() => handleSaveEdit(provider.id)}
										onCancelEdit={() => handleCancelEdit(provider.id)}
										onToggleOrgExpand={(orgId) =>
											handleToggleOrgExpand(provider.id, orgId)
										}
										onOrgChange={(orgId, field, value) =>
											handleOrgChange(provider.id, orgId, field, value)
										}
										onLocationChange={(orgId, locId, field, value) =>
											handleLocationChange(
												provider.id,
												orgId,
												locId,
												field,
												value,
											)
										}
										onRemoveOrg={(orgId) =>
											handleRemoveOrgRequest(provider.id, orgId)
										}
										onRemoveLocation={(orgId, locId) =>
											handleRemoveLocationRequest(provider.id, orgId, locId)
										}
									/>
								))}
								{totalPages > 1 && (
									<Flex
										padding="lg"
										justify="center"
										gap="lg"
										borderTopWidth="md"
										borderColor="sandA4"
									>
										<Button
											variant={currentPage === 1 ? "ghost" : "outline"}
											size="sm"
											onPress={
												currentPage === 1
													? undefined
													: () => setCurrentPage((p) => Math.max(1, p - 1))
											}
										>
											Previous
										</Button>
										<Button
											variant={currentPage === totalPages ? "ghost" : "outline"}
											size="sm"
											onPress={
												currentPage === totalPages
													? undefined
													: () =>
															setCurrentPage((p) => Math.min(totalPages, p + 1))
											}
										>
											Next
										</Button>
									</Flex>
								)}
							</>
						)}
					</Flex>
				</Dialog>
			</Modal>

			<ConfirmDialog
				isOpen={!!pendingDeleteOrg}
				title="Remove organization?"
				message={`Remove ${pendingOrgToDelete?.companyName || "this organization"} and all its locations?`}
				confirmLabel="Remove"
				onConfirm={confirmRemoveOrg}
				onCancel={() => setPendingDeleteOrg(undefined)}
			/>

			<ConfirmDialog
				isOpen={!!pendingDeleteLocation}
				title="Remove location?"
				message={`Remove ${pendingLocationToDelete?.loc?.addressLine1 || "this location"} from ${pendingLocationToDelete?.org?.companyName || "the organization"}?`}
				confirmLabel="Remove"
				onConfirm={confirmRemoveLocation}
				onCancel={() => setPendingDeleteLocation(undefined)}
			/>
		</>
	);
}
